<script setup lang="ts">
const props = defineProps({
  username: {
    type: {} as PropType<Username>,
    required: false,
  },
  id: {
    type: String,
    required: false,
    default: "",
  },
  fontSize: {
    type: String,
    required: false,
    default: "text-lg",
  },
  specialText: {
    type: Boolean,
    required: false,
    default: false,
  },
  truncate: {
    type: Boolean,
    required: false,
    default: false,
  },
  showTooltip: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const { fontSize, id } = toRefs(props);
const { getUserProfile } = useUserController();
const userStore = useUserStore();
const isLoading = ref(true);
const username = computed(() => {
  if (props.username)
    return {
      displayName: props.username.displayName,
      tagline: props.username.tagline,
      roles: props.username.roles || [],
    };
  if (!id.value) return { displayName: "", tagline: "", roles: [] };
  if (id.value === userStore.id)
    return {
      displayName: userStore.displayName,
      tagline: userStore.tagline,
      roles: userStore.roles,
    };
  let userProfile = useUserCache().getUserProfileFromCache(id.value);
  if (userProfile) {
    return {
      displayName: userProfile.displayName,
      tagline: userProfile.tagline,
      roles: userProfile.roles || [],
    };
  }
  return {
    displayName: fetchedUsername.value.displayName,
    tagline: fetchedUsername.value.tagline,
    roles: fetchedUsername.value.roles,
  };
});

const tagLineFontSize = computed(() => {
  if (fontSize.value === "text-sm") {
    return "text-xs";
  } else if (fontSize.value === "text-md") {
    return "text-sm";
  } else if (fontSize.value === "text-base") {
    return "text-md";
  } else if (fontSize.value === "text-lg") {
    return "text-base";
  } else if (fontSize.value === "text-xl") {
    return "text-lg";
  } else if (fontSize.value === "text-2xl") {
    return "text-xl";
  } else {
    return fontSize.value;
  }
});

const specialTextColor = computed(() => {
  if (props.specialText) {
    return "text-[#FFB800]";
  } else {
    return "text-neutral-300";
  }
});

const routingToProfile = (id: string) => {
  if (!id) {
    return "";
  } else if (id === userStore.id) {
    return `/collection/${useAppStateStore().currentMerchant}`;
  } else if (id) {
    return `/user/${id}`;
  } else {
    return "";
  }
};

const {
  data: fetchedUsername,
  status,
  execute: fetchUsername,
} = await useAsyncData(
  `username/${id.value}`,
  async () => {
    try {
      const response = await getUserProfile(id.value);
      if (!response.payload) {
        return { displayName: "", tagline: "", roles: [] };
      }
      return {
        displayName: response.payload.displayName,
        tagline: response.payload.tagline,
        roles: response.payload.roles || [],
      };
    } catch (error) {
      console.error("Error fetching username", error);
      return { displayName: "", tagline: "", roles: [] };
    }
  },
  {
    default: () => ({
      displayName: "",
      tagline: "",
      roles: null,
    }),
    dedupe: "defer",
    immediate: false,
  }
);

const init = async () => {
  try {
    if (props.username) return;
    if (!id.value) return;
    await fetchUsername();
  } finally {
    isLoading.value = false;
  }
};

init();
</script>

<template>
  <div
    v-if="status === 'pending'"
    class="w-36 h-5 rounded-sm bg-gray-700 animate-pulse"></div>
  <div v-else class="flex items-center gap-2">
    <NuxtLinkLocale
      v-if="!props.truncate"
      :to="routingToProfile(id)"
      class="flex items-baseline">
      <p
        class="font-semibold text-center group-hover:text-primary-500 font-nunito"
        :class="`${fontSize} ${specialTextColor}`">
        {{ username.displayName }}
      </p>
      <p
        v-if="username.tagline"
        class="font-semibold text-center group-hover:text-primary-500"
        :class="`${tagLineFontSize} ${specialTextColor}`">
        {{ "#" + username.tagline }}
      </p>
    </NuxtLinkLocale>
    <NuxtLinkLocale
      v-else
      :to="routingToProfile(id)"
      class="flex items-baseline">
      <p
        class="font-semibold text-center group-hover:text-primary-500 truncate"
        :class="`${fontSize} ${specialTextColor}`">
        {{
          username.displayName +
          (username.tagline ? "#" + username.tagline : "")
        }}
      </p>
    </NuxtLinkLocale>
    <BaseIconInfluencerBadge
      v-if="username.roles && username.roles.includes('influencer')"
      :show-tooltip="showTooltip" />
  </div>
</template>
